import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { ChevronLeft, ChevronRight, ShoppingBag, Star } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import EstoreChrome from "../components/EstoreChrome";
import { brand } from "../data/estoreContent";
import { getEstoreProductById, effectivePrice, estoreCatalog } from "../data/estoreCatalog";
import { ESTORE_ROUTES, estoreProductUrl } from "../data/estorePaths";
import { addToEstoreCart } from "../utils/estoreCart";

function EstoreProduct() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const id = params.get("id");
  const product = getEstoreProductById(id);
  const [imgIndex, setImgIndex] = useState(0);

  if (!product) {
    return (
      <Box sx={{ mt: 7, minHeight: "100vh", bgcolor: brand.bg }}>
        <EstoreChrome title="Product" showBack />
        <Container sx={{ py: 8, textAlign: "center" }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Product not found</Typography>
          <Button variant="contained" onClick={() => navigate(ESTORE_ROUTES.shop)}>Back to shop</Button>
        </Container>
      </Box>
    );
  }

  const images = product.images?.length ? product.images : [product.image];
  const price = effectivePrice(product);
  const related = estoreCatalog.filter((p) => p.id !== product.id && p.category === product.category).slice(0, 3);

  const handleAdd = () => {
    addToEstoreCart({
      productId: product.id,
      title: product.title,
      image: product.image,
      unitPrice: price,
      qty: 1,
    });
  };

  return (
    <Box sx={{ mt: 7, minHeight: "100vh", bgcolor: brand.bg }}>
      <EstoreChrome title={product.title} showBack />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <Box
                sx={{
                  position: "relative",
                  borderRadius: 3,
                  overflow: "hidden",
                  border: `1px solid ${brand.stroke}`,
                  aspectRatio: "1/1",
                  bgcolor: "#fff",
                }}
              >
                <Box
                  component="img"
                  src={images[imgIndex]}
                  alt={product.title}
                  sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 12,
                    left: 12,
                    right: 12,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Button size="small" variant="contained" sx={{ minWidth: 0, px: 1 }} onClick={() => setImgIndex((i) => (i - 1 + images.length) % images.length)}>
                    <ChevronLeft size={18} />
                  </Button>
                  <Button size="small" variant="contained" sx={{ minWidth: 0, px: 1 }} onClick={() => setImgIndex((i) => (i + 1) % images.length)}>
                    <ChevronRight size={18} />
                  </Button>
                </Box>
              </Box>
              <Grid container spacing={1} sx={{ mt: 1 }}>
                {images.map((src, i) => (
                  <Grid item xs={3} key={src}>
                    <Box
                      component="button"
                      type="button"
                      onClick={() => setImgIndex(i)}
                      sx={{
                        p: 0,
                        border: i === imgIndex ? `2px solid ${brand.accent}` : `1px solid ${brand.stroke}`,
                        borderRadius: 2,
                        overflow: "hidden",
                        cursor: "pointer",
                        width: "100%",
                        bgcolor: "#fff",
                      }}
                    >
                      <Box component="img" src={src} alt="" sx={{ width: "100%", aspectRatio: "1", objectFit: "cover", display: "block" }} />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </motion.div>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="overline" sx={{ color: brand.muted, fontWeight: 700 }}>
              {product.brand} · {product.category}
            </Typography>
            <Typography variant="h4" fontWeight={800} sx={{ mt: 1, color: brand.text, letterSpacing: "-0.02em" }}>
              {product.title}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 1.5 }}>
              <Star size={18} fill="#FBBF24" color="#FBBF24" />
              <Typography variant="body1" fontWeight={700}>{product.rating.toFixed(1)}</Typography>
            </Box>
            <Box sx={{ mt: 2, display: "flex", alignItems: "baseline", gap: 1.5 }}>
              <Typography variant="h5" fontWeight={800}>₹{price}</Typography>
              {product.discount > 0 && (
                <Typography sx={{ textDecoration: "line-through", color: brand.muted }}>₹{product.price}</Typography>
              )}
              {product.discount > 0 && (
                <Typography variant="body2" sx={{ color: "success.main", fontWeight: 700 }}>{product.discount}% off</Typography>
              )}
            </Box>
            <Typography variant="body1" sx={{ mt: 3, color: brand.muted, lineHeight: 1.75 }}>
              {product.description}
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 4 }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<ShoppingBag size={20} />}
                onClick={handleAdd}
                sx={{
                  borderRadius: 999,
                  px: 3,
                  fontWeight: 800,
                  textTransform: "none",
                  background: `linear-gradient(135deg, ${brand.accent} 0%, ${brand.accent2} 100%)`,
                }}
              >
                Add to bag
              </Button>
              <Button variant="outlined" size="large" onClick={() => navigate(ESTORE_ROUTES.cart)} sx={{ borderRadius: 999, px: 3, fontWeight: 700, textTransform: "none", borderColor: brand.stroke, color: brand.text }}>
                View bag
              </Button>
            </Stack>
          </Grid>
        </Grid>

        {related.length > 0 && (
          <Box sx={{ mt: 6 }}>
            <Typography variant="h6" fontWeight={800} sx={{ mb: 2 }}>
              More in {product.category}
            </Typography>
            <Grid container spacing={2}>
              {related.map((p) => (
                <Grid item xs={12} sm={4} key={p.id}>
                  <Card elevation={0} onClick={() => navigate(estoreProductUrl(p.id))} sx={{ borderRadius: 3, border: `1px solid ${brand.stroke}`, cursor: "pointer" }}>
                    <Box component="img" src={p.image} alt={p.title} sx={{ width: "100%", height: 180, objectFit: "cover" }} />
                    <CardContent>
                      <Typography fontWeight={800}>{p.title}</Typography>
                      <Typography variant="body2" sx={{ color: brand.muted }}>₹{effectivePrice(p)}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Container>
    </Box>
  );
}

export default EstoreProduct;
